import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArtifactProto, ChatResponse, ExecutionStepProto } from "build-ts/chat_session";
import { ChatMessageType, chatMessageTypeFromJSON, ExecutionStatusType, ArtifactType } from "build-ts/common";
import { ScheduledJobProto } from "build-ts/schedule";
import { DarwinToolType } from "build-ts/darwin_agent";
import { getStreamingLabel } from "utils/helperFuncUtils";
import showToast from "hooks/useCustomToast";
import { ArtifactOpenOpts, ChatMessage, ReasoningGroup, TimelineKind } from "../../interfaces";

// ── Private types ─────────────────────────────────────────────────────────────

interface ReasoningStreamState {
  /** The reasoning group index currently being streamed (-1 = none). */
  currentIndex: number;
  /** Server-side start times keyed by reasoning group index, used to compute per-group duration. */
  groupStartTimes: Map<number, number>;
  /**
   * Timestamp from the most recent EXECUTING_LLM_CALL step; used as startTimeMs of the first
   *  reasoning group so TTFT is included in "Thought for Xs".
   */
  llmCallStartMs: number | null;
  /**
   * Timestamp of the most recently processed execution step; used as end time
   * when closeActiveReasoningGroup is called.
   */
  lastStepTimeMs: number | null;
  /** Counter for silent thought groups. Uses values ≤ -100 to avoid collision with real indices (0, 1, 2…). */
  silentGroupIndex: number;
  /** How many tool runs have started this turn, used to assign their idx in timelineOrder. */
  nextToolRunIdx: number;
  /**
   * RESPONSE tokens that may be retroactively promoted to a thought block
   * if a subsequent step or reasoning event arrives.
   */
  pendingResponseText: string;
  /**
   * Counter for promoted reasoning groups. Uses values ≤ -200 to avoid collision with real (0+)
   * and silent (≤ -100) indices.
   */
  promotedReasoningIndex: number;
  /**
   * Timestamp when the first RESPONSE token of the current pending accumulation arrived;
   * used as startTimeMs when promoting to a thought block.
   */
  pendingResponseStartMs: number | null;
}

// Returns the index of the last group in the array whose .index matches idx.
// Used everywhere we need to target the most-recently created group — multiple
// groups can share the same index after a RESPONSE/EXECUTION_STEP reset.
const findLastReasoningGroupIdx = (groups: ReasoningGroup[], idx: number): number => {
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i]!.index === idx) return i;
  }
  return -1;
};

// ── Public types ──────────────────────────────────────────────────────────────

export interface UseChatStreamingParams {
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  registerArtifacts?: (...artifacts: ArtifactProto[]) => void;
  setScenarioJobsSavedByTools?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
}

export interface UseChatStreamingReturn {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  streamingStatusLabel: string;
  setStreamingStatusLabel: React.Dispatch<React.SetStateAction<string>>;
  /** Clears all retry and reasoning state — call at the start of every new send turn. */
  prepareForNewTurn: () => void;
  /** Flushes the token buffer, collapses open reasoning groups, and resets per-turn counters. */
  finalizeStreamingTurn: () => void;
  appendStreamingToken: (token: string) => void;
  handleExecutionStep: (step: ExecutionStepProto) => void;
  handleReasoningToken: (token: string, index: number) => void;
  applyStreamChunk: (chunk: ChatResponse, setResponse: (value: ChatResponse) => void) => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useChatStreaming({
  setChatMessages,
  registerArtifacts,
  setScenarioJobsSavedByTools,
  onArtifactClick,
}: UseChatStreamingParams): UseChatStreamingReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [streamingStatusLabel, setStreamingStatusLabel] = useState("Thinking ...");

  // ── Streaming refs ────────────────────────────────────────────────────────
  // Accumulates incoming streamed text tokens before we commit them to UI in batches.
  const tokenBufferRef = useRef("");
  // Tracks the currently scheduled requestAnimationFrame id for token flush batching.
  const tokenFlushRafRef = useRef<number | null>(null);
  // Stable ref to onArtifactClick so handleExecutionStep doesn't need it as a dep.
  const onArtifactClickRef = useRef(onArtifactClick);

  // ── Reasoning streaming state (single consolidated ref) ──────────────────
  const reasoningRef = useRef<ReasoningStreamState>({
    currentIndex: -1,
    groupStartTimes: new Map(),
    llmCallStartMs: null,
    lastStepTimeMs: null,
    silentGroupIndex: -100,
    nextToolRunIdx: 0,
    pendingResponseText: "",
    promotedReasoningIndex: -200,
    pendingResponseStartMs: null,
  });

  useEffect(() => {
    onArtifactClickRef.current = onArtifactClick;
  }, [onArtifactClick]);

  // Resets all per-turn reasoning counters to their initial values.
  const resetReasoningState = useCallback(() => {
    const rs = reasoningRef.current;
    rs.currentIndex = -1;
    rs.groupStartTimes.clear();
    rs.llmCallStartMs = null;
    rs.lastStepTimeMs = null;
    rs.silentGroupIndex = -100;
    rs.nextToolRunIdx = 0;
    rs.pendingResponseText = "";
    rs.pendingResponseStartMs = null;
    rs.promotedReasoningIndex = -200;
  }, []);

  // ── Token batching helpers ────────────────────────────────────────────────

  // Drains tokenBufferRef into the last AI message in a single setChatMessages call.
  // Also cancels any pending RAF so callers that need an immediate flush (e.g. when
  // promoting a RESPONSE block to a thought block) don't get a stale update after.
  const flushBufferedTokens = useCallback(() => {
    if (tokenFlushRafRef.current !== null) {
      cancelAnimationFrame(tokenFlushRafRef.current);
      tokenFlushRafRef.current = null;
    }
    const bufferedToken = tokenBufferRef.current;
    if (!bufferedToken) return;
    tokenBufferRef.current = "";
    setChatMessages(prev => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (updated[lastIdx]?.author === "ai") {
        updated[lastIdx] = {
          ...updated[lastIdx],
          content: (updated[lastIdx].content || "") + bufferedToken,
        };
      }
      return updated;
    });
  }, [setChatMessages]);

  // Debounces React state updates for incoming token chunks: instead of calling
  // setChatMessages on every token (potentially hundreds per second), we accumulate
  // tokens in tokenBufferRef and schedule a single flush on the next animation frame.
  // The guard (tokenFlushRafRef.current !== null) ensures only one RAF is queued at
  // a time — any additional tokens that arrive before the frame fires just append to
  // the buffer and piggyback on the already-scheduled flush.
  const scheduleTokenFlush = useCallback(() => {
    if (tokenFlushRafRef.current !== null) return;
    tokenFlushRafRef.current = window.requestAnimationFrame(() => {
      tokenFlushRafRef.current = null;
      flushBufferedTokens();
    });
  }, [flushBufferedTokens]);

  // Closes the currently active reasoning group (sets isOpen:false + durationMs) and
  // resets the tracking ref.  Must be called whenever a non-REASONING event arrives
  // so the label immediately changes from "Thought ..." to "Thought for Xs".
  const closeActiveReasoningGroup = useCallback(
    (endTimeMs?: number) => {
      const rs = reasoningRef.current;
      const activeIndex = rs.currentIndex;
      if (activeIndex === -1) return;
      const startTime = rs.groupStartTimes.get(activeIndex);
      const closeTime = endTimeMs ?? Date.now();
      const durationMs = startTime !== undefined ? closeTime - startTime : undefined;
      rs.currentIndex = -1;

      setChatMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.author !== "ai") return prev;
        const msg = updated[lastIdx];
        const groups = msg.streamingReasoningGroups ? [...msg.streamingReasoningGroups] : [];
        const groupIdx = findLastReasoningGroupIdx(groups, activeIndex);
        if (groupIdx >= 0) {
          groups[groupIdx] = { ...groups[groupIdx]!, isOpen: false, durationMs };
          updated[lastIdx] = { ...msg, streamingReasoningGroups: groups };
        }
        return updated;
      });
    },
    [setChatMessages]
  );

  // If RESPONSE-type tokens were accumulated but a STEP or REASONING event arrives
  // before the stream ends, those tokens were actually the model's thinking output.
  // This helper moves them out of the response content and into a closed thought block.
  // endTimeMs is the server-side step timestamp passed by handleExecutionStep.
  // When present it lets us compute an accurate duration and also suppresses the
  // silent thought group that handleExecutionStep would otherwise create for the
  // same LLM call — the promoted block already represents that thinking time.
  const promoteResponseToThoughtBlock = useCallback(
    (endTimeMs?: number) => {
      const rs = reasoningRef.current;
      const pendingText = rs.pendingResponseText;
      if (!pendingText) return;
      rs.pendingResponseText = "";

      // Prefer the LLM-call start (covers the case where no REASONING came before);
      // fall back to when the first RESPONSE token arrived (covers the case where a
      // REASONING block already consumed llmCallStartMs before RESPONSE tokens started).
      // Clearing llmCallStartMs here prevents handleExecutionStep from also creating
      // a silent group for the same LLM call — the promoted block already covers it.
      const startMs = rs.llmCallStartMs ?? rs.pendingResponseStartMs;
      rs.llmCallStartMs = null;
      rs.pendingResponseStartMs = null;
      // Cancel any pending RAF flush and discard the unflushed token buffer —
      // the full text is captured in pendingText, so nothing is lost.
      if (tokenFlushRafRef.current !== null) {
        cancelAnimationFrame(tokenFlushRafRef.current);
        tokenFlushRafRef.current = null;
      }
      tokenBufferRef.current = "";

      const promotedIndex = rs.promotedReasoningIndex--;
      const now = Date.now();
      const effectiveStartMs = startMs ?? now;
      // Fall back to Date.now() when no server timestamp is available (e.g. called from
      // handleReasoningToken, or the step carries no timestamp_ms). The duration will
      // use client-side time in that case, but at least it will always be shown.
      const endMs = endTimeMs ?? now;
      const durationMs = startMs !== null ? endMs - startMs : undefined;

      setChatMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.author !== "ai") return prev;
        const msg = updated[lastIdx]!;
        const groups = msg.streamingReasoningGroups ? [...msg.streamingReasoningGroups] : [];
        const newGroupIdx = groups.length;
        // Promoted groups are closed immediately — we have the full text up front.
        groups.push({
          index: promotedIndex,
          text: pendingText,
          isOpen: false,
          startTimeMs: effectiveStartMs,
          durationMs,
        });
        const newTimelineOrder = [...(msg.timelineOrder ?? []), { kind: TimelineKind.REASONING, idx: newGroupIdx }];
        return [
          ...updated.slice(0, lastIdx),
          { ...msg, content: "", streamingReasoningGroups: groups, timelineOrder: newTimelineOrder },
        ];
      });
    },
    [setChatMessages]
  );

  const appendStreamingToken = useCallback(
    (token: string) => {
      if (!token) return;
      // A RESPONSE token ends the current reasoning session — close the active group
      // immediately so its label switches from "Thought ..." to "Thought for Xs".
      // Use the last known step timestamp so the duration stays on the server clock.
      closeActiveReasoningGroup(reasoningRef.current.lastStepTimeMs ?? undefined);
      // Track every response token so it can be retroactively promoted to a thought
      // block if a subsequent EXECUTION_STEP or REASONING event arrives.
      if (!reasoningRef.current.pendingResponseText) {
        // First token of a new pending accumulation — record when it arrived.
        reasoningRef.current.pendingResponseStartMs = Date.now();
      }
      reasoningRef.current.pendingResponseText += token;
      tokenBufferRef.current += token;
      if (tokenBufferRef.current.length >= parseInt(process.env.REACT_APP_TOKEN_BUFFER_SIZE || "512")) {
        flushBufferedTokens();
        return;
      }
      scheduleTokenFlush();
    },
    [flushBufferedTokens, scheduleTokenFlush]
  );

  // Flushes buffered tokens, collapses any open reasoning groups, adds a silent thought
  // group if the final LLM call produced no reasoning tokens, then resets all per-turn
  // counters. Replaces the identical ~40-line cleanup block in each streaming branch.
  const finalizeStreamingTurn = useCallback(() => {
    flushBufferedTokens();
    const rs = reasoningRef.current;
    const streamEndTimeMs = rs.lastStepTimeMs ?? Date.now();
    // Capture before reset so the setChatMessages closure can reference them.
    const pendingSilentStartMs = rs.llmCallStartMs;
    const pendingSilentIndex = --rs.silentGroupIndex;
    resetReasoningState();
    setChatMessages(prev => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (updated[lastIdx]?.author !== "ai") return updated;
      const msg = updated[lastIdx];
      let groups = msg.streamingReasoningGroups ? [...msg.streamingReasoningGroups] : [];
      let timelineOrder = msg.timelineOrder;
      // If the final LLM call produced no reasoning, add a silent closed thought group.
      if (pendingSilentStartMs !== null) {
        const newGroupIdx = groups.length;
        groups.push({
          index: pendingSilentIndex,
          text: "",
          isOpen: false,
          startTimeMs: pendingSilentStartMs,
          durationMs: streamEndTimeMs - pendingSilentStartMs,
        });
        timelineOrder = [...(timelineOrder ?? []), { kind: TimelineKind.REASONING, idx: newGroupIdx }];
      }
      // Collapse any groups still open (stream ended mid-reasoning).
      groups = groups.map(g => {
        if (!g.isOpen) return g;
        return {
          ...g,
          isOpen: false,
          durationMs: g.durationMs === undefined ? streamEndTimeMs - g.startTimeMs : g.durationMs,
        };
      });
      const nextGroups = groups.length ? groups : msg.streamingReasoningGroups;
      return [...updated.slice(0, lastIdx), { ...msg, streamingReasoningGroups: nextGroups, timelineOrder }];
    });
  }, [flushBufferedTokens, resetReasoningState, setChatMessages]);

  // Drain pending token buffer on unmount
  useEffect(() => {
    return () => {
      const bufferedToken = tokenBufferRef.current;
      if (bufferedToken) {
        tokenBufferRef.current = "";
        setChatMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.author === "ai") {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: (updated[lastIdx].content || "") + bufferedToken,
            };
          }
          return updated;
        });
      }
      if (tokenFlushRafRef.current !== null) {
        cancelAnimationFrame(tokenFlushRafRef.current);
      }
    };
  }, [setChatMessages]);

  // ── Reasoning token handler ───────────────────────────────────────────────

  /**
   * Called for each REASONING-type token arriving from the stream.
   * Same index → append token directly to the open thought block.
   * New index  → close the previous block (recording its duration), open a fresh one.
   *
   * No RAF batching is used here: reasoning tokens arrive less frequently than
   * response tokens, and direct updates prevent any end-of-stream truncation.
   */
  const handleReasoningToken = useCallback(
    (token: string, index: number) => {
      if (!token) return;
      // If RESPONSE tokens arrived before this real REASONING token, they were
      // the model's thinking — promote them to a thought block now.
      promoteResponseToThoughtBlock();
      const rs = reasoningRef.current;
      const prevIndex = rs.currentIndex;

      if (prevIndex === index) {
        // Same reasoning group: append token to the open block.
        setChatMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.author !== "ai") return prev;
          const msg = updated[lastIdx];
          const groups = msg.streamingReasoningGroups ? [...msg.streamingReasoningGroups] : [];
          const groupIdx = findLastReasoningGroupIdx(groups, index);
          if (groupIdx >= 0) {
            groups[groupIdx] = { ...groups[groupIdx], text: groups[groupIdx]!.text + token };
            updated[lastIdx] = { ...msg, streamingReasoningGroups: groups };
          }
          return updated;
        });
      } else {
        // New reasoning group: close the previous one and open a fresh one.
        const now = Date.now();

        // If an EXECUTING_LLM_CALL timestamp was stored, use it as the start so the
        // displayed "Thought for Xs" duration includes TTFT (time-to-first-token).
        const effectiveStartTime = rs.llmCallStartMs ?? now;
        rs.llmCallStartMs = null;

        const prevStartTime = rs.groupStartTimes.get(prevIndex);
        const prevDurationMs = prevIndex >= 0 && prevStartTime !== undefined ? now - prevStartTime : undefined;
        rs.groupStartTimes.set(index, effectiveStartTime);
        rs.currentIndex = index;

        setChatMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.author !== "ai") return prev;
          const msg = updated[lastIdx];
          const groups = msg.streamingReasoningGroups ? [...msg.streamingReasoningGroups] : [];

          // Close the most-recent group for prevIndex and record its duration.
          if (prevIndex >= 0) {
            const prevGroupIdx = findLastReasoningGroupIdx(groups, prevIndex);
            if (prevGroupIdx >= 0) {
              groups[prevGroupIdx] = {
                ...groups[prevGroupIdx]!,
                isOpen: false,
                durationMs: prevDurationMs,
              };
            }
          }

          // Open a new group and record its position in the unified timeline order.
          const newGroupIdx = groups.length;
          groups.push({ index, text: token, isOpen: true, startTimeMs: effectiveStartTime });
          const newTimelineOrder = [...(msg.timelineOrder ?? []), { kind: TimelineKind.REASONING, idx: newGroupIdx }];
          updated[lastIdx] = { ...msg, streamingReasoningGroups: groups, timelineOrder: newTimelineOrder };
          return updated;
        });
      }
    },
    [setChatMessages, promoteResponseToThoughtBlock]
  );

  // ── Execution step handler ────────────────────────────────────────────────

  const handleExecutionStep = useCallback(
    (step: ExecutionStepProto) => {
      // Compute step time first so it can be forwarded to promoteResponseToThoughtBlock
      // for an accurate duration on the promoted thought block.
      const stepTimeMs = step.timestamp_ms !== undefined ? Number(step.timestamp_ms) : undefined;
      reasoningRef.current.lastStepTimeMs = stepTimeMs ?? null;

      // If RESPONSE tokens were buffered before this step arrived, they were the
      // model's thinking output — promote them to a thought block with the step
      // timestamp so the duration is accurate and no silent group is duplicated.
      promoteResponseToThoughtBlock(stepTimeMs);
      setStreamingStatusLabel(getStreamingLabel(step));

      if (step.status === ExecutionStatusType.EXECUTING_LLM_CALL) {
        // Close any previous reasoning group using the server timestamp, then store this
        // step's timestamp so the first real reasoning group can include TTFT in its duration.
        closeActiveReasoningGroup(stepTimeMs);
        reasoningRef.current.llmCallStartMs = stepTimeMs ?? Date.now();
      } else {
        // Any other execution-step event ends the current reasoning session.
        closeActiveReasoningGroup(stepTimeMs);

        // If llmCallStartMs is still set, the LLM call that just ended produced no
        // reasoning tokens. Create a silent closed thought group so the timeline shows
        // the thinking time and the breakdown sums to the total "Worked for" duration.
        if (reasoningRef.current.llmCallStartMs !== null) {
          const rs = reasoningRef.current;
          const silentStartMs = rs.llmCallStartMs!;
          const silentDurationMs = stepTimeMs !== undefined ? stepTimeMs - silentStartMs : undefined;
          const silentIndex = --rs.silentGroupIndex;
          rs.llmCallStartMs = null;
          setChatMessages(prev => {
            const updated = [...prev];
            for (let j = updated.length - 1; j >= 0; j--) {
              if (updated[j].author === "ai") {
                const msg = updated[j];
                const groups = msg.streamingReasoningGroups ? [...msg.streamingReasoningGroups] : [];
                const newGroupIdx = groups.length;
                groups.push({
                  index: silentIndex,
                  text: "",
                  isOpen: false,
                  startTimeMs: silentStartMs,
                  durationMs: silentDurationMs,
                });
                const newTimelineOrder = [
                  ...(msg.timelineOrder ?? []),
                  { kind: TimelineKind.REASONING, idx: newGroupIdx },
                ];
                updated[j] = { ...msg, streamingReasoningGroups: groups, timelineOrder: newTimelineOrder };
                break;
              }
            }
            return updated;
          });
        }
      }

      // When a new tool invocation starts, record its position in the unified timeline order.
      if (step.status === ExecutionStatusType.EXECUTING_TOOL_CALL) {
        const runIdx = reasoningRef.current.nextToolRunIdx++;
        setChatMessages(prev => {
          const updated = [...prev];
          for (let j = updated.length - 1; j >= 0; j--) {
            if (updated[j].author === "ai") {
              updated[j] = {
                ...updated[j],
                timelineOrder: [...(updated[j].timelineOrder ?? []), { kind: TimelineKind.RUN, idx: runIdx }],
              };
              break;
            }
          }
          return updated;
        });
      }

      // File upload is a standalone completed run — record it in the timeline and auto-open
      // the artifact panel for each uploaded file.
      if (step.status === ExecutionStatusType.CREATING_FILE_ARTIFACT) {
        const runIdx = reasoningRef.current.nextToolRunIdx++;
        setChatMessages(prev => {
          const updated = [...prev];
          // Attach file artifact protos to the last user message.
          for (let j = updated.length - 1; j >= 0; j--) {
            if (updated[j].author === "user") {
              updated[j] = {
                ...updated[j],
                fileArtifacts: [...(updated[j].fileArtifacts ?? []), ...step.artifacts],
              };
              break;
            }
          }
          // Record the timeline run on the last AI message.
          for (let j = updated.length - 1; j >= 0; j--) {
            if (updated[j].author === "ai") {
              updated[j] = {
                ...updated[j],
                timelineOrder: [...(updated[j].timelineOrder ?? []), { kind: TimelineKind.RUN, idx: runIdx }],
              };
              break;
            }
          }
          return updated;
        });
        if (step.artifacts.length > 0 && onArtifactClickRef.current) {
          for (const artifact of step.artifacts) {
            onArtifactClickRef.current(artifact, undefined);
          }
        }
      }

      if (step.status === ExecutionStatusType.ERROR_STATE) {
        setIsLoading(false);
        showToast("Something went wrong", "The response could not be completed", "error");
      }

      // Auto-open the artifact panel as soon as a tool call produces artifacts.
      if (
        step.status === ExecutionStatusType.TOOL_CALL_SUCCESS &&
        step.artifacts.length > 0 &&
        onArtifactClickRef.current
      ) {
        for (const artifact of step.artifacts) {
          let activeTab: string | undefined;
          let subResourceName: string | undefined;
          if (artifact.artifact_type === ArtifactType.DATAFRAME_ANALYSIS) {
            activeTab = "results";
            const dfa = artifact.dataframe_analysis;
            // Priority: reports[0] > charts[0] > pandas_dataframes[0]
            if (dfa?.reports && dfa.reports.length > 0 && dfa.reports[0]?.name) {
              subResourceName = dfa.reports[0].name;
            } else if (dfa?.charts && dfa.charts.length > 0 && dfa.charts[0]?.name) {
              subResourceName = dfa.charts[0].name;
            } else if (dfa?.pandas_dataframes && dfa.pandas_dataframes.length > 0 && dfa.pandas_dataframes[0]?.name) {
              subResourceName = dfa.pandas_dataframes[0].name;
            }
          }
          onArtifactClickRef.current(
            artifact,
            activeTab || subResourceName ? { activeTab, subResourceName } : undefined
          );
        }
      }

      // Append the step to executionSteps urgently (outside startTransition) so that the
      // terminal step (TOOL_CALL_SUCCESS / TOOL_CALL_FAILURE / ERROR_STATE) is in the list
      // before the next render. If this were deferred, setIsLoading(false) would cause a
      // render with isStreaming=false while the last run still has completed:false, making
      // toolRunLabelParts fall through to the in-progress label ("Writing analysis code").
      setChatMessages(prev => {
        const updated = [...prev];
        for (let j = updated.length - 1; j >= 0; j--) {
          if (updated[j].author === "ai") {
            const next: (typeof updated)[0] = { ...updated[j] };
            next.executionSteps = [...(next.executionSteps ?? []), step];
            const flat = (next.executionSteps ?? []).flatMap(s => s.artifacts);
            next.artifacts = flat.length > 0 ? flat : undefined;
            updated[j] = next;
            break;
          }
        }
        return updated;
      });

      // Artifact registration and scenario job updates are non-urgent — keep them deferred
      // so they don't block the token streaming UI.
      React.startTransition(() => {
        if (step.artifacts.length > 0 && registerArtifacts) {
          registerArtifacts(...step.artifacts);
        }

        const savingArtifact = step.artifacts.find(a => a.scenario_job);
        if (
          (step.status === ExecutionStatusType.SAVING_SCENARIO ||
            (step.status === ExecutionStatusType.TOOL_CALL_SUCCESS &&
              step.tool_type === DarwinToolType.SAVE_SCENARIO_TOOL)) &&
          savingArtifact?.scenario_job
        ) {
          setScenarioJobsSavedByTools?.([savingArtifact.scenario_job]);
        }
      });
    },
    [registerArtifacts, setChatMessages, setScenarioJobsSavedByTools, promoteResponseToThoughtBlock]
  );

  // ── Stream chunk handler ──────────────────────────────────────────────────

  // Processes a ChatResponse chunk that arrives at the end of a streaming turn
  // (the final "done" payload, not individual token events). It does two things:
  //   1. Forwards the full chunk to setResponse so the caller can react to it.
  //   2. Reconciles the last AI ChatMessage with the authoritative server state:
  //        REASONING message → writes executionSteps, artifacts, and trace URLs
  //                            (the tool-call metadata that streaming tokens don't carry).
  //        RESPONSE message  → overwrites content with the server's canonical text,
  //                            or sets responseMessage for structured response tools.
  // Wrapped in React.startTransition so the reconciliation render is lower-priority
  // than the live token updates and doesn't block the streaming UI.
  const applyStreamChunk = useCallback(
    (chunk: ChatResponse, setResponse: (value: ChatResponse) => void) => {
      setResponse(chunk);
      if (!chunk.messages?.length) return;
      React.startTransition(() => {
        const assistantMessages = chunk.messages.filter(m => {
          const type = chatMessageTypeFromJSON(m.message_type);
          return type === ChatMessageType.REASONING || type === ChatMessageType.RESPONSE;
        });
        if (!assistantMessages.length) return;

        const reasoningMsg = [...assistantMessages]
          .reverse()
          .find(m => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.REASONING);
        const responseMsg = [...assistantMessages]
          .reverse()
          .find(m => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.RESPONSE);

        if (reasoningMsg) {
          const validArtifacts = reasoningMsg.execution_steps.flatMap(s => s.artifacts);
          if (validArtifacts.length > 0 && registerArtifacts) {
            registerArtifacts(...validArtifacts);
          }
          setChatMessages(prev => {
            const updated = [...prev];
            for (let j = updated.length - 1; j >= 0; j--) {
              if (updated[j].author === "ai") {
                updated[j] = {
                  ...updated[j],
                  executionSteps: reasoningMsg.execution_steps.length > 0 ? reasoningMsg.execution_steps : undefined,
                  artifacts: validArtifacts.length > 0 ? validArtifacts : undefined,
                  ls_trace_url: reasoningMsg.ls_trace_url,
                  langgraph_message_json_list: reasoningMsg.langgraph_message_json_list?.length
                    ? reasoningMsg.langgraph_message_json_list
                    : [],
                };
                break;
              }
            }
            return updated;
          });
        }

        if (responseMsg) {
          setChatMessages(prev => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (updated[lastIdx]?.author === "ai") {
              if (responseMsg.content) {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: responseMsg.content,
                  ls_trace_url: responseMsg.ls_trace_url,
                };
              } else if (responseMsg.response_message) {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: "",
                  responseMessage: responseMsg.response_message,
                  ls_trace_url: responseMsg.ls_trace_url,
                };
              }
            }
            return updated;
          });
        }
      });
    },
    [registerArtifacts, setChatMessages]
  );

  // ── Turn lifecycle helpers ────────────────────────────────────────────────

  // Clears all streaming and reasoning state at the start of each new send turn.
  const prepareForNewTurn = useCallback(() => {
    tokenBufferRef.current = "";
    resetReasoningState();
  }, [resetReasoningState]);

  return {
    isLoading,
    setIsLoading,
    streamingStatusLabel,
    setStreamingStatusLabel,
    prepareForNewTurn,
    finalizeStreamingTurn,
    appendStreamingToken,
    handleExecutionStep,
    handleReasoningToken,
    applyStreamChunk,
  };
}
